import React from 'react'

import Box from 'components_new/atoms/Box'
import Checkbox from 'components_new/atoms/Checkbox'
import Collapse from 'components_new/atoms/Collapse'
import FormControlLabel from 'components_new/atoms/FormControlLabel'
import Icon from 'components_new/atoms/Icon'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import IconButton from 'components_new/molecules/IconButton'

import DataRerunJobName from 'components_new/organisms/internal/DataReruns/JobName'

import { formatCostUsd } from 'components_new/organisms/internal/DataRuns/utils'
import {
  RerunCandidate,
  RerunJobRef
} from 'redux/selectors/internal/DataReruns'

import { estimateRow, formatEstimateMinutes, formatJobScope } from './lib'

interface RerunCustomerRowProps {
  candidate: RerunCandidate
  disabled: boolean
  expanded: boolean
  /** The collection jobs that are selected. */
  jobNames: string[]
  onToggle: () => void
  onToggleExpanded: () => void
  onToggleJob: (jobName: string) => void
  onTogglePriority: () => void
  priority: boolean
  selected: boolean
}

const LockedJob = (props: { job: RerunJobRef; note: string }) => (
  <FormControlLabel
    control={<Checkbox checked disabled size="small" />}
    label={
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
        <DataRerunJobName
          jobName={props.job.job_name}
          label={props.job.label}
        />
        <Text sx={{ color: 'text.secondary' }} variant="caption">
          {props.note}
        </Text>
        <Icon fontSize="inherit" name="Lock" sx={{ color: 'text.secondary' }} />
      </Box>
    }
    labelPlacement="end"
  />
)

const RerunCustomerRow = (props: RerunCustomerRowProps) => {
  const {
    candidate,
    disabled,
    expanded,
    jobNames,
    onToggle,
    onToggleExpanded,
    onToggleJob,
    onTogglePriority,
    priority,
    selected
  } = props

  const total = candidate.collection_jobs.length
  const downstream = candidate.downstream_jobs.length
  const expandable = !disabled && total + downstream > 0
  const { costUsd, minutes } = estimateRow(candidate, jobNames)
  const estimate = [
    formatEstimateMinutes(minutes),
    formatCostUsd(costUsd)
  ].join(' · ')

  return (
    <Box
      sx={{
        '&:not(:first-of-type)': {
          borderTop: '1px solid',
          borderTopColor: 'divider'
        }
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          opacity: selected ? 1 : 0.6,
          pl: 0.5,
          pr: 1,
          py: 0.5
        }}
      >
        <Checkbox
          checked={selected}
          disabled={disabled}
          onChange={onToggle}
          size="small"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Text sx={{ fontWeight: 600 }} variant="body2">
              {candidate.customer_name}
            </Text>
            {candidate.sim_running ? (
              <Tag
                color="warning"
                label="Kör nu"
                startIconName="WarningAmberOutlined"
              />
            ) : null}
            {candidate.queued_in_rerun ? (
              <Tag
                color="warning"
                label="Köad i annan omkörning"
                startIconName="WarningAmberOutlined"
              />
            ) : null}
          </Box>
          <Text sx={{ color: 'text.secondary' }} variant="caption">
            {formatJobScope(jobNames.length, total, downstream)} · {estimate}
          </Text>
        </Box>
        <IconButton
          disabled={disabled}
          name={priority ? 'Star' : 'StarBorderOutlined'}
          onClick={onTogglePriority}
          size="small"
          sx={{ color: priority ? 'warning.main' : 'text.secondary' }}
          title={priority ? 'Går först' : 'Låt kunden gå först'}
        />
        <IconButton
          disabled={!expandable}
          name={expanded ? 'ExpandLess' : 'ExpandMore'}
          onClick={onToggleExpanded}
          size="small"
          title="Jobb"
        />
      </Box>

      <Collapse in={expanded && expandable} unmountOnExit>
        <Box
          sx={{
            bgcolor: 'action.hover',
            display: 'flex',
            flexDirection: 'column',
            pb: 1,
            pl: 5.5,
            pr: 2,
            pt: 0.5
          }}
        >
          {candidate.collection_jobs.map((job) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={jobNames.includes(job.job_name)}
                  onChange={() => onToggleJob(job.job_name)}
                  size="small"
                />
              }
              key={job.job_name}
              label={
                <DataRerunJobName jobName={job.job_name} label={job.label} />
              }
              labelPlacement="end"
            />
          ))}
          <LockedJob job={candidate.sim_job} note="körs alltid" />
          {candidate.downstream_jobs.map((job) => (
            <LockedJob job={job} key={job.job_name} note="startas av SIM" />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

RerunCustomerRow.displayName = 'RerunCustomerRow'
export default RerunCustomerRow
